import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, apiErrorMessage, callFunction } from "../api/client";
import type { SubmittedUrl, SubmittedUrlRecord } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { orAbsent, shortDate } from "../lib/format";
import { RecordLink } from "../components/RecordLink";
import { Spinner } from "../components/Spinner";
import { Link } from "react-router-dom";
import { CAPTURE_ZIP } from "../lib/capture";
import { csvFilename, downloadCsv, toCsv } from "../lib/csv";

/* Hand the engine a page to read.
 *
 * Deliberately not attached to whatever record was on screen a moment ago.
 * Filing a page against a company because that is where somebody happened to be
 * asserts a relationship the page might not support, and the engine works out
 * for itself which companies, people and properties a page is about. */
export function SubmitUrl({ session }: { session: SignedIn }) {
  const [url, setUrl] = useState("");
  const [said, setSaid] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [sending, setSending] = useState(false);
  const [crawl, setCrawl] = useState(false);
  const queryClient = useQueryClient();

  const queue = useQuery({
    queryKey: ["submitted-urls"],
    refetchInterval: 30000,
    queryFn: async () => {
      const { data, error } = await apiClient(session.idToken)
        .from("submitted_url")
        .select("id,url,submitted_by,submitted_at,updated_at,state,reason,triage_score,characters_read,records_linked")
        .order("submitted_at", { ascending: false, nullsFirst: false })
        .limit(50);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as SubmittedUrl[];
    },
  });

  /* What each page produced, for the whole queue in one request rather than
     one per row. The count on screen was a dead number: a page that made three
     records said so and there was no way to see which three. */
  const made = useQuery({
    queryKey: ["submitted-url-records"],
    queryFn: async () => {
      const { data, error } = await apiClient(session.idToken)
        .from("submitted_url_record")
        .select("signal_id,entity_type,entity_id,label")
        .limit(500);
      if (error) throw new Error(apiErrorMessage(error));
      const by: Record<string, SubmittedUrlRecord[]> = {};
      for (const r of (data ?? []) as SubmittedUrlRecord[]) {
        if (!r.signal_id) continue;
        (by[r.signal_id] ||= []).push(r);
      }
      return by;
    },
  });

  async function send() {
    setSending(true);
    setSaid(null);
    setFailed(false);
    try {
      await callFunction<string>(session.idToken, "submit_url",
                                 { p_url: url.trim(), p_crawl: crawl });
      setSaid(crawl
        ? "Added, with the rest of the site. Watch the queue below."
        : "Added. Watch the queue below for what happens to it.");
      setUrl("");
      queryClient.invalidateQueries({ queryKey: ["submitted-urls"] });
    } catch (e) {
      setFailed(true);
      setSaid(e instanceof Error ? e.message : "it was not accepted");
    } finally {
      setSending(false);
    }
  }

  return (
    <main>
      <h1>Submit a page</h1>
      <p className="muted">
        Paste the address of a page worth reading: a permit notice, an
        announcement, a management company's site. The engine reads it and
        works out for itself which records it is about.
      </p>
      {/* The browser button sends the page on screen, which reaches pages
          behind a sign-in that an address alone cannot. James asked for the
          download here, with the steps a click away. */}
      <p className="bar">
        <a className="button-link" href={CAPTURE_ZIP} download>Download EVIE Capture</a>
        <Link to="/browser-button">How to install it</Link>
      </p>

      <div className="bar">
        <label htmlFor="u">Address</label>
        <input
          id="u" value={url} style={{ width: "28rem" }}
          onChange={(e) => { setUrl(e.target.value); setSaid(null); }}
          onKeyDown={(e) => { if (e.key === "Enter" && url.trim()) void send(); }}
        />
        <button onClick={send} disabled={sending || url.trim().length < 12}>
          {sending ? <><Spinner />Sending</> : "Send it"}
        </button>
      </div>
      {/* A building's own site is worth more than one page of it. The engine
          walks up to twenty, and the toggle is off by default because twenty
          pages is twenty readings and the queue is shared. */}
      <div className="bar">
        <label>
          <input type="checkbox" checked={crawl}
                 onChange={(e) => setCrawl(e.target.checked)} />
          {" "}Read the whole site, up to 20 pages
        </label>
      </div>
      {said ? <p className={failed ? "refusal" : "muted"} role="status">{said}</p> : null}

      <h2>
        What happened to them
        {queue.data?.length ? (
          <button
            className="row-action" style={{ marginLeft: "0.6rem", fontWeight: 400 }}
            onClick={() => downloadCsv(csvFilename("submitted-pages"), toCsv(
              ["url", "state", "reason", "triage_score", "records_linked", "submitted_by", "submitted_at"],
              (queue.data ?? []) as unknown as Record<string, unknown>[]))}
          >
            CSV
          </button>
        ) : null}
      </h2>
      {queue.isLoading ? <p className="muted"><Spinner />Loading.</p>
        : queue.isError ? <p className="refusal">{(queue.error as Error).message}</p>
        : !queue.data?.length ? (
          <p className="muted">
            Nothing has been submitted yet. A page sent here goes through the
            same reading the engine does on everything it finds itself.
          </p>
        ) : (
          <table>
            <thead>
              <tr><th>Page</th><th>State</th><th>Why</th><th>Records</th><th>By</th><th>Sent</th></tr>
            </thead>
            <tbody>
              {queue.data.map((s) => (
                <tr key={s.id}>
                  <td style={{ maxWidth: "22rem", wordBreak: "break-all" }}>
                    {s.url ? <a href={s.url} target="_blank" rel="noreferrer">{s.url}</a> : orAbsent(s.url)}
                  </td>
                  <td>
                    {/* Four states that mean nothing came of it, and they are
                        four different facts. Red on all of them so a person
                        sees at a glance that nothing arrived, and the reason
                        column says which one it was.
                        "read, nothing found" was added by engine migration 084
                        and is the one that used to read as "rejected" with an
                        empty reason: the page was fetched and read in full and
                        the extractor made no record of it, which is something
                        a person can act on. */}
                    {s.state === "rejected" || s.state === "could not be fetched"
                      || s.state === "read, nothing found"
                      ? <span className="refusal">{s.state}</span>
                      : s.state === "waiting"
                        ? <span className="muted"><Spinner />waiting</span>
                        : s.state}
                  </td>
                  <td className="muted" style={{ maxWidth: "20rem" }}>
                    {/* Never silence. Triage dropping the one link somebody
                        chose on purpose, with no reason shown, is the failure
                        this column exists to prevent. */}
                    {s.reason
                      ? <>{s.reason}{s.triage_score ? ` (scored ${s.triage_score} of 5)` : ""}</>
                      : s.state === "read" && s.characters_read
                        ? `${s.characters_read.toLocaleString()} characters read`
                        : s.state === "waiting" ? "not read yet" : ""}
                  </td>
                  {/* The records themselves, not a number nobody can open. */}
                  <td>
                    {(made.data?.[s.id ?? ""] ?? []).length
                      ? (made.data?.[s.id ?? ""] ?? []).map((r, i) => (
                          <span key={`${r.entity_type}-${r.entity_id}`}>
                            {i ? ", " : ""}
                            {/* A project has no list of its own in this
                                release, so it renders as its name. The other
                                three link to their record. */}
                            {r.entity_type === "company" || r.entity_type === "person"
                             || r.entity_type === "property" ? (
                              <RecordLink
                                to={r.entity_type === "company" ? "companies"
                                    : r.entity_type === "person" ? "people" : "properties"}
                                id={r.entity_id} label={r.label} />
                            ) : (
                              orAbsent(r.label)
                            )}
                          </span>
                        ))
                      : <span className="muted">-</span>}
                  </td>
                  <td className="muted">{orAbsent(s.submitted_by)}</td>
                  <td>{shortDate(s.submitted_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </main>
  );
}
